/**
 * @fileoverview Statistics filter extension: filter by product tags.
 * @module shared/components/filters/extensions/TagsFilterExtension
 */

import { type ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Tags } from 'lucide-react'
import { useFilter } from '@/shared/hooks/useFilter'
import { MultiCombobox, type ComboboxOption } from '@/shared/components/filters/MultiCombobox'

/**
 * Renders a multi-select over the available product tags, wired to the global
 * filter context. Selecting tags narrows statistics to products carrying any of
 * the chosen tags (backend `?tags=<uuid>,<uuid>` filter).
 *
 * @component
 */
export function TagsFilterExtension(): ReactElement {
  const { t } = useTranslation('common')
  const { tags, setTags, availableTags, isLoadingTags } = useFilter()

  const tagOptions: ComboboxOption[] = useMemo(
    () => (availableTags ?? []).map(tag => ({ value: tag.uuid, text: tag.name })),
    [availableTags]
  )

  return (
    <MultiCombobox
      options={tagOptions}
      selectedValues={tags}
      onChange={setTags}
      placeholder={t('filters.searchTags')}
      disabled={isLoadingTags}
      isLoading={isLoadingTags}
      className="shrink-0"
      triggerIcon={<Tags className="size-4" />}
    />
  )
}
